

import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faSearch,
  faSortAmountDown,
  faSortAmountUp,
  faFileExport,
  faSpinner,
  faExclamationTriangle,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import apiHelper from "../ApiHelpar/apiHelper";
import {
  GET_ALL_HOSPITAL_BRANCH_BY_STATUS,
  SERCH_BRANCH_BY_NAME_WITH_PAGINATION,
  STATUS_CHANGE_BY_BRANCH_ID,
} from "../visitConstant/ApiConstant";
import useDebounce from "../useDebounce/useDebounce";
import swal from "sweetalert";

const stylebg = {
  backgroundColor: "#f8f9fa",
  paddingTop: 18,
  paddingBottom: 18,
  fontSize: 14,
  color: "#6c757d",
  whiteSpace: "nowrap",
};

const BranchList = ({
  expandedView,
  setDataEdit,
  hospitalId,
  BaranchId,
  Role,
  RefreshApi,
}) => {
  // State management
  const [hospitalList, setHospitalList] = useState([]);
  const [sortField, setSortField] = useState("branchName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataListActive, setDataListActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  // Debounce search term to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Memoized fetch function to prevent unnecessary re-creations
  const fetchHospitals = useCallback(
    async (page = currentPage, pageSize = itemsPerPage) => {
      setIsLoading(true);
      setError("");

      try {
        /// GET_ALL_HOSPITAL_BRANCH_BY_STATUS;
        const offset = (page - 1) * pageSize;
        const response = await apiHelper(SERCH_BRANCH_BY_NAME_WITH_PAGINATION, {
          hospitalId: hospitalId,
          name: debouncedSearchTerm,
          offset,
          pageSize,
          activeStatus: dataListActive,
          // sortField,
          // sortDirection: sortDirection.toUpperCase(),
        });

        if (response?.respInfo?.code === "0") {
          setHospitalList(response.respData?.respMsg.content || []);
          setTotalItems(response.respData?.respMsg.totalElements || 0);

        
        } else {
          throw new Error(
            response?.respInfo?.message || "Failed to fetch hospital branches"
          );
        }
      } catch (error) {
        console.error("Error fetching hospital branches:", error);
        setError("Failed to load hospital branches. Please try again.");
        setHospitalList([]);
        setTotalItems(0);
      } finally {
        setIsLoading(false);
        setInitialLoad(false);
      }
    },
    [
      debouncedSearchTerm,
      dataListActive,
      sortField,
      sortDirection,
      itemsPerPage,
    ]
  );

  // Handle initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchHospitals(1, itemsPerPage);
  }, []);

  // Reset to first page when search/filter changes
  useEffect(() => {
    if (!initialLoad) {
      setCurrentPage(1);
      fetchHospitals(1, itemsPerPage);
    }
  }, [
    debouncedSearchTerm,
    dataListActive,
    sortField,
    sortDirection,
    itemsPerPage,
    
  ]);

  // Fetch data when page changes
  useEffect(() => {
    if (!initialLoad) {
      fetchHospitals();
    }
  }, [currentPage, RefreshApi]);

  // Sorting handler with visual feedback
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(newDirection);
    setSortField(field);
  };

  // Status change handler with better error handling
  const handleStatusChange = async (hospital) => {
    swal({
      title: "Are you sure?",
      text: `This will ${
        hospital.active ? "deactivate" : "activate"
      } the branch "${hospital.branchName}"!`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (confirmed) {
        try {
          setIsLoading(true);

          const response = await apiHelper(STATUS_CHANGE_BY_BRANCH_ID, {
            id: hospital.id,
            active: !hospital.active,
          });

          if (response?.respInfo?.code === "0") {
            await fetchHospitals();
            swal("Success!", `Branch status updated successfully.`, "success");
          } else {
            throw new Error(
              response?.respInfo?.message || "Failed to update status"
            );
          }
        } catch (error) {
          console.error("Error updating branch status:", error);
          swal("Error!", `Failed to update status: ${error.message}`, "error");
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  // Export to CSV with error handling and loading state
  const exportToCSV = async () => {
    try {
      setIsLoading(true);

      const response = await apiHelper(GET_ALL_HOSPITAL_BRANCH_BY_STATUS, {
        name: debouncedSearchTerm,
        offset: 0,
        pageSize: totalItems || 1000, // Fallback limit
        active: dataListActive,
        sortField,
        sortDirection: sortDirection.toUpperCase(),
      });

      if (response?.respInfo?.code === "0") {
        const dataToExport = response.respData?.respMsg.content || [];

        if (dataToExport.length === 0) {
          swal("No Data", "There are no records to export.", "info");
          return;
        }

        const csvContent = [
          "ID,Branch Name,Branch Address,Status,Hospital Name,Registration Date,Closing Date",
          ...dataToExport.map((branch) => {
            // Safely handle undefined values and format properly for CSV
            const safeFormat = (value) => {
              if (value === undefined || value === null) return '""';
              return `"${String(value).replace(/"/g, '""')}"`;
            };

            const formattedDate = (dateStr) => {
              if (!dateStr) return '""';
              try {
                return `"${moment(dateStr).format("DD-MMM-YYYY")}"`;
              } catch {
                return '""';
              }
            };

            return [
              branch.id || "",
              safeFormat(branch.branchName),
              safeFormat(branch.branchAddress),
              branch.active ? '"Active"' : '"Inactive"',
              safeFormat(branch.hospital?.name),
              formattedDate(branch.registrationDate),
              formattedDate(branch.closingDate),
            ].join(",");
          }),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `hospital_branches_${moment().format(
          "YYYY-MM-DD"
        )}.csv`;
        link.click();

        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);

        swal("Success", "Data exported successfully", "success");
      } else {
        throw new Error(response?.respInfo?.message || "Failed to export data");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      swal("Error!", `Failed to export data: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate pagination variables
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const showingStart =
    totalItems === 0
      ? 0
      : Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const showingEnd = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers with ellipsis for better UX
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust range if current page is near start or end
      if (currentPage <= 3) {
        endPage = 4;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis indicator for skipped pages
      if (startPage > 2) {
        pages.push("...");
      }

      // Add range pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis for end skip
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // Safe truncate function for text
  const truncateText = (text, length = 20) => {
    if (!text) return "";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  // Format date safely
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return moment(dateStr).format("DD-MMM-YYYY");
    } catch {
      return "-";
    }
  };
  const handlePerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  return (
    <div className="container-fluid p-0">
      {/* Header Section with improved layout */}
      <div className="row align-items-center mb-3 px-3 mt-3">
        <div className="col-md-6 col-lg-8 mb-3 mb-md-0">
          <div className="d-flex align-items-center">
            <div className="input-group">
              <span
                className={`input-group-text cursor-pointer ${
                  dataListActive ? "bg-white" : "bg-primary text-white"
                }`}
                onClick={() => setDataListActive(!dataListActive)}
                title={
                  dataListActive
                    ? "Showing Active Branches"
                    : "Showing Inactive Branches"
                }
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search branch name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <span className="ms-2 badge bg-info">
              {dataListActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
        <div className="col-md-6 col-lg-4">
          <div className="d-flex justify-content-md-end">
            <button
              onClick={exportToCSV}
              className="btn btn-outline-secondary"
              disabled={isLoading || totalItems === 0}
              title="Export to CSV"
            >
              <FontAwesomeIcon icon={faFileExport} className="me-2" />
              Export
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => fetchHospitals(currentPage)}
              disabled={isLoading}
              title="Refresh data"
            >
              <FontAwesomeIcon icon={faSync} spin={isLoading} />
            </button>
          </div>
        </div>
      </div>

      {/* Error message display */}
      {error && (
        <div className="alert alert-danger mx-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="text-center my-3">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="2x"
            className="text-primary"
          />
          <p className="mt-2">Loading branches...</p>
        </div>
      )}

      {/* Table Section with improved sorting and handling */}
      {!isLoading && (
        <div className="table-responsive">
          <table className="table table-hover">
            {/* Table Headers with sorting indicators */}
            <thead>
              <tr>
                <th style={stylebg} className="rounded-start">
                  #
                </th>
                <th
                  style={{ ...stylebg, cursor: "pointer" }}
                  onClick={() => handleSort("branchName")}
                  className="position-relative"
                >
                  Branch Name
                  {sortField === "branchName" && (
                    <FontAwesomeIcon
                      icon={
                        sortDirection === "asc"
                          ? faSortAmountUp
                          : faSortAmountDown
                      }
                      className="ms-2 text-primary"
                      size="sm"
                    />
                  )}
                </th>
                <th style={stylebg}>Address</th>
                <th
                  style={{ ...stylebg, cursor: "pointer" }}
                  onClick={() => handleSort("active")}
                  className="position-relative"
                >
                  Status
                  {sortField === "active" && (
                    <FontAwesomeIcon
                      icon={
                        sortDirection === "asc"
                          ? faSortAmountUp
                          : faSortAmountDown
                      }
                      className="ms-2 text-primary"
                      size="sm"
                    />
                  )}
                </th>
                <th
                  style={{ ...stylebg, cursor: "pointer" }}
                  onClick={() => handleSort("hospital.name")}
                  className="position-relative"
                >
                  Hospital Name
                  {sortField === "hospital.name" && (
                    <FontAwesomeIcon
                      icon={
                        sortDirection === "asc"
                          ? faSortAmountUp
                          : faSortAmountDown
                      }
                      className="ms-2 text-primary"
                      size="sm"
                    />
                  )}
                </th>
                <th
                  style={{ ...stylebg, cursor: "pointer" }}
                  onClick={() => handleSort("registrationDate")}
                  className="position-relative"
                >
                  Reg. Date
                  {sortField === "registrationDate" && (
                    <FontAwesomeIcon
                      icon={
                        sortDirection === "asc"
                          ? faSortAmountUp
                          : faSortAmountDown
                      }
                      className="ms-2 text-primary"
                      size="sm"
                    />
                  )}
                </th>
                <th style={stylebg}>Closing Date</th>
                <th style={stylebg} className="rounded-end">
                  Actions
                </th>
              </tr>
            </thead>

            {/* Table Body with safely handled data */}
            <tbody>
              {hospitalList.map((branch, index) => (
                <tr key={branch.id}>
                  <td>{showingStart + index}</td>
                  <td>
                    <span title={branch.branchName}>
                      {truncateText(branch.branchName, 30)}
                    </span>
                  </td>
                  <td>
                    <span title={branch.branchAddress}>
                      {truncateText(branch.branchAddress, 15)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        branch.active ? "bg-success" : "bg-danger"
                      }`}
                    >
                      {branch.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <span title={branch.hospital?.name}>
                      {truncateText(branch.hospital?.name, 25) || "-"}
                    </span>
                  </td>
                  <td>{formatDate(branch.registrationDate)}</td>
                  <td>{formatDate(branch.closingDate)}</td>
                  <td>
                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setDataEdit(branch)}
                        title="Edit Branch"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className={`btn btn-sm ${
                          branch.active ? "btn-danger" : "btn-success"
                        }`}
                        onClick={() => handleStatusChange(branch)}
                        title={
                          branch.active
                            ? "Deactivate Branch"
                            : "Activate Branch"
                        }
                      >
                        <FontAwesomeIcon
                          icon={branch.active ? faTrash : faSearch}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty State with better messaging */}
          {hospitalList.length === 0 && !isLoading && (
            <div className="text-center py-5">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size="2x"
                className="text-muted mb-3"
              />
              <h5>No branches found</h5>
              <p className="text-muted">
                {searchTerm
                  ? `No branches match your search "${searchTerm}"`
                  : `No ${
                      dataListActive ? "active" : "inactive"
                    } branches found`}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pagination Section with improved UI */}
      {totalItems > 0 && !isLoading && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 mt-3 mb-3">
          <div className="text-muted mb-3 mb-md-0">
            Showing {showingStart} to {showingEnd} of {totalItems} entries
          </div>
          <nav aria-label="Branch list pagination">
            <ul className="pagination pagination-sm mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
              </li>

              {getPageNumbers().map((page, index) => (
                <li
                  key={index}
                  className={`page-item ${
                    page === "..."
                      ? "disabled"
                      : currentPage === page
                      ? "active"
                      : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => page !== "..." && setCurrentPage(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </li>

              <li>
                <select
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={handlePerPageChange}
                  style={{ width: "auto" }}
                >
                  {[5, 10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default BranchList;